import { Baby, Landmark, ShieldAlert, HeartHandshake, ExternalLink } from "lucide-react";
import { Reveal, Eyebrow } from "./primitives";
import { STORY } from "@/lib/content";

export default function Story() {
  return (
    <section id="story" data-testid="story-section" className="bg-sand py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        {/* Header */}
        <div className="max-w-3xl">
          <Reveal>
            <Eyebrow>{STORY.eyebrow}</Eyebrow>
            <h2 className="mt-6 font-serif text-4xl font-medium leading-tight tracking-tight text-ink sm:text-5xl">
              {STORY.heading}
            </h2>
            <p className="mt-6 text-base leading-relaxed text-stone sm:text-lg">{STORY.intro}</p>
          </Reveal>
          <Reveal
            delay={0.06}
            className="mt-6 flex items-start gap-3 rounded-2xl border border-rust/25 bg-rust/10 p-5"
            data-testid="story-restraining-order"
          >
            <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-rust" strokeWidth={2.2} />
            <p className="text-sm leading-relaxed text-ink sm:text-base">{STORY.restrainingOrder}</p>
          </Reveal>
        </div>

        {/* Timeline */}
        <div className="mt-16 max-w-3xl">
          <Reveal>
            <div className="relative border-l border-line pl-8">
              {STORY.timeline.map((t, i) => (
                <div key={i} className="relative pb-12 last:pb-0" data-testid={`story-timeline-${i}`}>
                  <span className="absolute -left-[41px] flex h-6 w-6 items-center justify-center rounded-full bg-terracotta ring-4 ring-sand">
                    <span className="h-2 w-2 rounded-full bg-white" />
                  </span>
                  <p className="text-sm font-bold uppercase tracking-[0.18em] text-sage">{t.date}</p>
                  <h3 className="mt-2 font-serif text-2xl font-medium text-ink">{t.title}</h3>
                  {t.body && (
                    <p className="mt-2 max-w-lg text-base leading-relaxed text-stone">{t.body}</p>
                  )}
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        {/* Two callouts */}
        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2">
          <Reveal data-testid="story-natalia" className="rounded-3xl bg-ink p-8 text-bone sm:p-10">
            <div className="flex items-center gap-3">
              <Baby className="h-6 w-6 text-terracotta" strokeWidth={2} />
              <span className="text-sm font-bold uppercase tracking-[0.18em] text-sage">
                {STORY.natalia.label}
              </span>
            </div>
            <p className="mt-5 font-serif text-xl italic leading-snug text-bone sm:text-2xl">
              {STORY.natalia.body}
            </p>
          </Reveal>

          <Reveal
            delay={0.08}
            data-testid="story-coercion"
            className="rounded-3xl border border-terracotta/30 bg-terracotta/10 p-8 sm:p-10"
          >
            <div className="flex items-center gap-3">
              <Landmark className="h-6 w-6 text-terracotta" strokeWidth={2} />
              <span className="text-sm font-bold uppercase tracking-[0.18em] text-rust">
                {STORY.coercion.label}
              </span>
            </div>
            <p className="mt-5 text-base leading-relaxed text-ink sm:text-lg">{STORY.coercion.body}</p>
          </Reveal>
        </div>

        {/* Reflection */}
        <Reveal delay={0.06} className="mt-6 overflow-hidden rounded-3xl bg-sage/15" data-testid="story-reflection">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="order-2 p-8 sm:p-10 lg:order-1">
              <div className="flex items-center gap-3">
                <HeartHandshake className="h-6 w-6 text-terracotta" strokeWidth={2} />
                <span className="text-sm font-bold uppercase tracking-[0.18em] text-sage">
                  {STORY.reflection.label}
                </span>
              </div>
              <p className="mt-5 font-serif text-xl italic leading-snug text-ink sm:text-2xl">
                {STORY.reflection.body}
              </p>
              <a
                href={STORY.reflection.linkHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="story-reflection-link"
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-semibold text-bone transition-transform duration-300 hover:scale-105"
              >
                {STORY.reflection.linkLabel}
                <ExternalLink className="h-4 w-4" strokeWidth={2.2} />
              </a>
            </div>
            <div className="order-1 min-h-[240px] lg:order-2">
              <img
                src={STORY.reflection.image}
                alt="Sunlight breaking through clouds over the ocean"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
